<template>
  <OffcanvasBase
    v-model="showOverlay"
    header-class="border-bottom"
    body-class="m-0 p-0"
    title="Your Cart"
    shadow
    placement="end"
    class="c-checkout-overlay"
  >
    <div class="d-flex flex-column h-100">
      <div class="flex-grow-1 p-3">
        <div v-if="cartCountdownVisible" :class="cartCountdownAlertClass">
          <div class="d-flex align-items-center justify-content-between">
            <strong>Time remaining</strong>
            <span class="h5 mb-0">{{ cartFormattedRemaining }}</span>
          </div>
          <small v-if="cartIsExpired" class="d-block mt-1">
            Your cart has expired. Please re-add items to continue.
          </small>
        </div>
        <CartItem
          v-for="item in $store.state.cart.items"
          :key="item.id"
          :item="item"
          :loading-state="loadingState"
          :msg-exists-for-checkout-item="msgExistsForCheckoutItem"
          :msg-for-checkout-item="msgForCheckoutItem"
          @remove="removeItem"
          @quantity-update="
            ({ item, quantity }) => triggerQuantityUpdate(item, quantity)
          "
        />
        <div v-if="$store.state.cart.items.length === 0">
          <div class="alert alert-light text-center">
            You have no items in your cart.
          </div>
        </div>
        <AlertDanger v-if="cartError" show class="mt-3" data-test="cart-error">
          {{ cartError }}
        </AlertDanger>
      </div>
      <div
        v-if="$store.state.cart.items.length"
        class="d-flex justify-content-end"
      >
        <ButtonLink @click="clearCart">Clear cart</ButtonLink>
      </div>
      <CartSummaryFooter
        v-if="$store.state.cart.items.length"
        :checkout-total="checkoutTotal"
        :item-count="$store.state.cart.items.length"
        :tax-percent="taxPercent"
        :loading-state="loadingState"
        :can-checkout="canCheckout"
        @checkout="goToCheckout"
        @continue-shopping="setCartState(false)"
      />
    </div>
  </OffcanvasBase>
</template>

<script lang="ts">
import { OffcanvasBase } from '@/components/base/Offcanvas';
import { CartItem, CartSummaryFooter } from '@/components/feature';
import { AlertDanger } from '@/components/ui/Alert';
import ButtonLink from '@/components/ui/Button/ButtonLink.vue';
import { useMoney } from '@/composables/useMoney';
import axios from '@/lib/services/utils/Axios';
import debounce from 'lodash/debounce';
import truncate from 'lodash/truncate';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
export default {
  name: 'CheckoutOverlayContainer',
  components: {
    AlertDanger,
    OffcanvasBase,
    ButtonLink,
    CartItem,
    CartSummaryFooter,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    sessionId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      user: window.Spark,
      nowMs: Date.now(),
      cartCountdownIntervalId: null,
      cartExpiryHandled: false,
      cartExpiredAtMs: null,
    };
  },
  computed: {
    showOverlay: {
      get() {
        return this.displayCartOverlay;
      },
      set(val) {
        this.setCartState(val);
      },
    },
    ...mapState('cart', [
      'displayCartOverlay',
      'cartError',
      'taxPercent',
      'checkoutItemSidebarMsgs',
      'loadingState',
    ]),
    ...mapGetters('cart', ['checkoutTotal', 'latestCartItemCreatedAt']),
    cartRemainingSeconds() {
      if (this.cartExpiredAtMs !== null) return 0;
      if (!this.latestCartItemCreatedAt) return null;
      const createdAtMs = Date.parse(this.latestCartItemCreatedAt);
      if (!Number.isFinite(createdAtMs)) return null;

      const expiresAtMs = createdAtMs + 15 * 60 * 1000;
      const remaining = Math.floor((expiresAtMs - this.nowMs) / 1000);
      return Math.max(0, remaining);
    },
    cartIsExpired() {
      if (this.cartExpiredAtMs !== null) return true;
      if (!this.latestCartItemCreatedAt) return false;
      if (this.cartRemainingSeconds === null) return false;
      return this.cartRemainingSeconds <= 0;
    },
    cartCountdownVisible() {
      return (
        this.$store.state.cart.items.length > 0 &&
        (Boolean(this.latestCartItemCreatedAt) || this.cartExpiredAtMs !== null)
      );
    },
    cartFormattedRemaining() {
      if (this.cartRemainingSeconds === null) return '';
      const minutes = Math.floor(this.cartRemainingSeconds / 60);
      const seconds = this.cartRemainingSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    cartCountdownAlertClass() {
      const base = 'alert mb-3';
      if (this.cartIsExpired) return `${base} alert-danger`;
      if (
        this.cartRemainingSeconds !== null &&
        this.cartRemainingSeconds < 3 * 60
      ) {
        return `${base} alert-warning`;
      }
      return `${base} alert-info`;
    },
    canCheckout() {
      const hasServerItems =
        this.$store.state.cart.items.filter((item) => Boolean(item.id)).length >
        0;

      return (
        hasServerItems && this.loadingState === false && !this.cartIsExpired
      );
    },
  },
  watch: {
    cartIsExpired(newVal) {
      if (newVal) {
        this.handleCartExpiry();
      }
    },
    '$store.state.cart.items.length'(newVal) {
      if (newVal > 0) {
        this.cartExpiryHandled = false;
        this.cartExpiredAtMs = null;
      }
    },
  },
  async mounted() {
    this.cartCountdownIntervalId = window.setInterval(() => {
      this.nowMs = Date.now();
    }, 1000);

    let items = this.items;
    if (!this.user.userId) {
      await axios.post('/api/checkout/list').then((response) => {
        items = response.data.items;
      });
    }

    // Only add items if cart is empty to avoid duplicates
    if (this.$store.state.cart.items.length === 0) {
      items.map((item) => {
        this.addItem({
          id: item.id,
          type: item.type,
          amount: item.amount,
          name: item.name,
          description: item.description,
          currency: item.currency,
          metadata: item.metadata,
          uuid: item.uuid,
          depends_on: item.depends_on,
          quantity: item.quantity ?? 1,
          created_at: item.created_at,
        });
      });
    }

    this.setSessionId(this.sessionId);
    await this.determineTotal();

    if (this.cartIsExpired) {
      this.handleCartExpiry();
    }
  },
  beforeUnmount() {
    if (this.cartCountdownIntervalId) {
      window.clearInterval(this.cartCountdownIntervalId);
      this.cartCountdownIntervalId = null;
    }
  },
  methods: {
    ...useMoney(),
    ...mapMutations('cart', [
      'toggleCart',
      'addItem',
      'setSessionId',
      'setCartState',
    ]),
    ...mapActions('cart', [
      'createCheckoutItem',
      'removeItem',
      'determineTotal',
      'updateQuantity',
    ]),

    clearCart() {
      this.$store.state.cart.items.forEach((item) => {
        this.removeItem({ uuid: item.uuid, id: item.id });
      });
    },
    handleCartExpiry() {
      if (this.cartExpiryHandled) {
        return;
      }

      this.cartExpiryHandled = true;
      this.cartExpiredAtMs = Date.now();
      this.$store.commit('cart/setItems', []);
    },
    goToCheckout() {
      window.location.href = '/checkout';
    },
    truncatedText(text) {
      return truncate(text, {
        length: 50,
        omission: '...',
      });
    },
    formatBookingDateTime(startTime, endTime) {
      if (!startTime || !endTime) return '';

      const start = new Date(startTime);
      const end = new Date(endTime);

      const startDate = start.toLocaleDateString();
      const startTimeStr = start.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      const endTimeStr = end.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });

      return `${startDate} | ${startTimeStr} - ${endTimeStr}`;
    },
    triggerQuantityUpdate: debounce(function (item, quantity) {
      this.updateQuantity({
        ...item,
        quantity,
      });
    }, 250),
    msgExistsForCheckoutItem(id) {
      return this.checkoutItemSidebarMsgs.id === id;
    },
    msgForCheckoutItem(id) {
      if (this.msgExistsForCheckoutItem(id)) {
        return this.checkoutItemSidebarMsgs.msg;
      }

      return null;
    },
  },
};
</script>
